<template>
  <div class="container">
    <div class="wrapper" :class="{ 'drawer-open': configDrawerSettings.open }">
      <div class="prev">
        <div class="list">
          <NodeTreeSection
            v-if="sequence"
            :nodes="nodes"
            :sequence="sequence"
            @open-drawer="handleConfirmOpenDrawer"
            @close-drawer="handleCloseDrawer"
            @create="handleCreate"
            @update="emit('update:nodes')"
          />
        </div>
      </div>
    </div>

    <Transition name="slide-fade">
      <ConfigDrawerSection
        ref="configDrawerSection"
        v-if="configDrawerSettings.open"
        :nodes="nodes"
        :node="configDrawerSettings.node"
        :sequence="sequence"
        :saving="saving"
        :disabled="sequence?.status == SequenceStatus.Active"
        @close="handleCloseDrawer"
        @save="handleEdit"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { Ref, inject, reactive, ref, createVNode } from "vue";
import { useRoute } from "vue-router";

import {
  Sequence,
  SequenceNode,
  SequenceNodeAction,
  SequenceNodeType,
  SequenceStatus,
} from "@/@types/flows/sequences";
import { UpdateNodeDto } from "@/@types/flows/sequences/dto";
import { sequenceNodeService } from "@/app/flows/sequences/services/SequenceNodes.service";

import ConfigDrawerSection from "@/app/flows/sequences/components/sections/ConfigDrawerSection.vue";
import NodeTreeSection from "@/app/flows/sequences/components/sections/NodeTreeSection.vue";
import { message } from "ant-design-vue";
import { ResponseError } from "@/app/shared/utils/httpManager";
import { Modal } from "ant-design-vue";
import { QuestionCircleOutlined } from "@ant-design/icons-vue";

const props = defineProps<{ nodes: SequenceNode[]; sequence: Sequence | undefined }>();

const emit = defineEmits<{ "update:nodes": [] }>();

const configDrawerSettings: {
  open: boolean;
  node: SequenceNode | undefined;
} = reactive({ open: false, node: undefined });

const configDrawerSection = ref();

const currentOpenNode = inject("currentOpenNode") as Ref<number | undefined>;

const handleConfirmOpenDrawer = (node: SequenceNode) => {
  if (configDrawerSection.value?.saveDisabled === false) {
    Modal.confirm({
      title: "¿Deseas descartar los cambios realizados?",
      content: "Recuerda que si no guardas los cambios registrados, estos se perderán.",
      icon: createVNode(QuestionCircleOutlined),
      onOk: () => {
        handleOpenDrawer(node);
      },
      centered: true,
      okText: "Sí, descartar cambios",
      cancelText: "No, seguir editando",
    });
  } else {
    handleOpenDrawer(node);
  }
};

const handleOpenDrawer = (node: SequenceNode) => {
  currentOpenNode.value = node.id;
  configDrawerSettings.open = true;
  configDrawerSettings.node = node;
  if (configDrawerSection.value) {
    configDrawerSection.value.saveDisabled = true;
  }
};

const handleCloseDrawer = () => {
  currentOpenNode.value = undefined;
  configDrawerSettings.open = false;
};

const route = useRoute();
const saving = ref(false);

const handleEdit = async (nodeId: number, updatedNode: UpdateNodeDto) => {
  // const sequenceId = Number(route.params.id);

  saving.value = true;

  try {
    const index = props.nodes.findIndex((el) => el.id == nodeId);
    if (updatedNode.action != props.nodes[index].action) {
      if (index < props.nodes.length - 1 && configDrawerSettings.node) {
        // await sequenceNodeService.remove(configDrawerSettings.node, props.nodes);
        await sequenceNodeService.remove(props.nodes[index + 1], props.nodes);
      }

      if (props.sequence) {
        let newNodeType;
        if (updatedNode.action == SequenceNodeAction.MakeAutomaticCall) {
          newNodeType = SequenceNodeType.AutomaticCall;
        } else if (updatedNode.action == SequenceNodeAction.SendEmail) {
          newNodeType = SequenceNodeType.Email;
        } else if (updatedNode.action == SequenceNodeAction.SendWhatsapp) {
          newNodeType = SequenceNodeType.Whatsapp;
        } else if (updatedNode.action == SequenceNodeAction.SendSms) {
          newNodeType = SequenceNodeType.Sms;
        } else if (updatedNode.action == SequenceNodeAction.AddTimeInterval) {
          newNodeType = SequenceNodeType.TimeInterval;
        }

        console.log(index, "index");
        if (newNodeType) {
          const node_order = configDrawerSettings.node?.node_order || 0;
          await sequenceNodeService.create(props.sequence.id, newNodeType, props.sequence?.sequence_type, node_order + 1);
        }
      }
    }
    await sequenceNodeService.update(nodeId, updatedNode);
    emit("update:nodes");
    message.success("Nodo actualizado correctamente");
    handleCloseDrawer();
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";

    if (error instanceof ResponseError) errorMessage = error.getErrorMessage();

    message.error(errorMessage);
    console.log(error);
  }

  saving.value = false;
};

const handleCreate = async (nodeType: SequenceNodeType) => {
  const sequenceId = Number(route.params.id);

  try {
    await sequenceNodeService.create(sequenceId, nodeType, props.sequence?.sequence_type);
    emit("update:nodes");
  } catch (error) {
    console.error(error);
  }
};
</script>

<style scoped lang="sass">
.container
  display: flex
  justify-content: space-between
  gap: 12px
  height: 100%
  margin-top: 8px

.list
  overflow: auto
  overflow-x: hidden
  height: 100%
  max-height: calc(100vh - 167px)

.wrapper
  flex: 1 0 100%
  display: flex
  // justify-content: center
  transition: flex-basis 0.3s ease
  overflow: hidden

  &.drawer-open
    flex: 1 0 50%
    border-right: 1px solid #00000026
    border-top: 1px solid #00000026
    border-top-right-radius: 10px

.prev
  // margin-top: 12px
  flex: 1 1 auto
  // max-width: 50vw
  // background-color: white
  position: relative
  overflow: hidden
</style>
